import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Divider } from "@/components/MotifBackground";
import ShareButton from "@/components/ShareButton";
import Seo from "@/components/Seo";
import { ArrowLeft, ArrowRight, Utensils, Gift, Flame, Heart, Check } from "lucide-react";

const KANYA_IMG = "https://static.prod-images.emergentagent.com/jobs/f79bd64e-30d1-4119-8097-970fcb1907b0/images/51a77df7c3a5a5d221cd893727c0fd2917faf56d0c05834401e5ce649bbf9280.jpeg";

// Age-wise forms of the Devi honoured in the kanya (age 2–10), per the Devi tradition.
const AGE_FORMS = [
  { age: 2, deva: "कुमारिका", en: "Kumarika", boon_en: "Freedom from poverty; longevity", boon_hi: "दरिद्रता-नाश; आयु" },
  { age: 3, deva: "त्रिमूर्ति", en: "Trimurti", boon_en: "Prosperity and progeny", boon_hi: "धन-धान्य एवं संतति" },
  { age: 4, deva: "कल्याणी", en: "Kalyani", boon_en: "Auspiciousness and success", boon_hi: "मंगल एवं सफलता" },
  { age: 5, deva: "रोहिणी", en: "Rohini", boon_en: "Freedom from disease; health", boon_hi: "रोग-नाश; आरोग्य" },
  { age: 6, deva: "कालिका", en: "Kalika", boon_en: "Victory, courage, learning", boon_hi: "विजय, साहस, विद्या" },
  { age: 7, deva: "चण्डिका", en: "Chandika", boon_en: "Wealth and power", boon_hi: "ऐश्वर्य एवं शक्ति" },
  { age: 8, deva: "शाम्भवी", en: "Shambhavi", boon_en: "Victory in disputes; peace", boon_hi: "विवाद में विजय; शान्ति" },
  { age: 9, deva: "दुर्गा", en: "Durga", boon_en: "Victory over foes; fulfilment", boon_hi: "शत्रु पर विजय; इष्ट-सिद्धि" },
  { age: 10, deva: "सुभद्रा", en: "Subhadra", boon_en: "Fulfilment of all wishes", boon_hi: "समस्त मनोरथ की पूर्ति" },
];

const STEPS = [
  { en: "Clean the puja space and lay a fresh mat (asan) for the girls to be seated with honour.", hi: "पूजा-स्थान स्वच्छ कर कन्याओं हेतु आदरपूर्वक स्वच्छ आसन बिछाएँ।" },
  { en: "Respectfully invite nine girls aged 2–10; many families also invite one young boy as 'Batuk' / Bhairav.", hi: "2–10 वर्ष की नौ कन्याओं को सादर आमन्त्रित करें; अनेक परिवार एक बालक को 'बटुक'/भैरव रूप में भी बुलाते हैं।" },
  { en: "Wash their feet with clean water as a mark of reverence, then gently dry them.", hi: "श्रद्धा-स्वरूप उनके चरण स्वच्छ जल से धोकर कोमलता से पोंछें।" },
  { en: "Apply a tilak of roli/kumkum with akshat on the forehead, and tie mauli on the wrist if it is your tradition.", hi: "माथे पर रोली/कुंकुम एवं अक्षत का तिलक करें; परम्परा हो तो हाथ में मौली बाँधें।" },
  { en: "Offer flowers and aarti to Maa Durga first, then serve the girls the bhog — halwa, puri and black chana.", hi: "पहले माँ दुर्गा को पुष्प एवं आरती अर्पित करें, फिर कन्याओं को भोग — हलवा, पूरी एवं काले चने — परोसें।" },
  { en: "Let them eat peacefully and to their heart's content, without hurrying.", hi: "उन्हें बिना जल्दबाज़ी के शान्ति से भरपेट भोजन करने दें।" },
  { en: "Offer dakshina and gifts — clothes, bangles, a dupatta, sweets, toys or money.", hi: "दक्षिणा एवं उपहार अर्पित करें — वस्त्र, चूड़ियाँ, चुनरी, मिष्ठान्न, खिलौने अथवा द्रव्य।" },
  { en: "Touch their feet, take their blessings, and see them off with honour.", hi: "उनके चरण स्पर्श कर आशीर्वाद लें एवं ससम्मान विदा करें।" },
];

const BHOG = [
  { en: "Halwa (semolina / sooji)", hi: "हलवा (सूजी)" },
  { en: "Puri (deep-fried bread)", hi: "पूरी" },
  { en: "Black chana (kale chane)", hi: "काले चने" },
  { en: "A coconut or fruit, and sweets", hi: "नारियल अथवा फल एवं मिष्ठान्न" },
];

export default function KanyaPujan() {
  const { pick, isDeva } = useLang();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  return (
    <div data-testid="kanya-pujan-page" className="pb-24">
      <Seo path="/navratri/kanya-pujan" type="article"
        title="Kanya Pujan — Navratri Ashtami/Navami Ritual, Age-wise Devi Forms & Bhog"
        description="A complete Kanya Pujan guide for Navratri Ashtami/Navami — how to honour nine young girls as the living Goddess, the age-wise forms of the Devi (Kumarika to Subhadra), the bhog (halwa, puri, black chana) and the step-by-step vidhi."
        keywords="Kanya Pujan, Kanjak, Navratri Ashtami Navami, kanya pujan vidhi, age wise devi forms, halwa puri chana bhog, kumari puja" />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-line">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-14 md:grid-cols-2 md:gap-14 md:px-8 md:py-20">
          <div>
            <Link to="/navratri" data-testid="kanya-back" className="inline-flex items-center gap-1.5 text-sm text-ink-3 transition-colors hover:text-saffron">
              <ArrowLeft className="h-4 w-4" /> {pick("नवरात्रि पूजा", "Navratri Pooja")}
            </Link>
            <p className="mt-6 overline-label text-saffron">{pick("अष्टमी / नवमी", "Ashtami / Navami")}</p>
            <h1 className={`mt-3 text-4xl leading-[1.05] text-ink deva-lh md:text-6xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("कन्या पूजन", "Kanya Pujan")}</h1>
            <div className="mt-6 h-px w-20 bg-[var(--gold)]/60" />
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-2">
              {pick(
                "नवरात्रि के समापन पर नौ कन्याओं को साक्षात् देवी-स्वरूप मानकर पूजा जाता है — यही कन्या पूजन (कंजक) है। इसे नवरात्रि की सम्पूर्ण साधना का फल-स्वरूप माना गया है।",
                "As Navratri concludes, nine young girls are honoured as the living Goddess herself — this is Kanya Pujan (Kanjak). It is held to be the fruit and culmination of the entire nine-night sadhana."
              )}
            </p>
            <div className="mt-6"><ShareButton title="Kanya Pujan · Dharmartha" label={pick("साझा करें", "Share")} testid="kanya-share" /></div>
          </div>
          <div className="relative mx-auto w-full max-w-xl overflow-hidden border border-[var(--gold)]/50 bg-[var(--parchment)] shadow-[0_16px_44px_rgba(44,38,33,0.16)]">
            <img src={KANYA_IMG} alt={pick("कन्या पूजन", "Kanya Pujan")} className="block h-auto w-full object-cover" />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-5 md:px-8">
        {/* Age-wise forms */}
        <section className="mt-16 fade-up" data-testid="kanya-ageforms">
          <div className="mb-6 flex items-baseline gap-3">
            <span className="h-px w-10 bg-[var(--gold)]" />
            <h2 className={`text-3xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("आयु अनुसार देवी-स्वरूप", "The age-wise forms of the Devi")}</h2>
          </div>
          <p className="max-w-2xl text-ink-3">{pick("परम्परा में 2 से 10 वर्ष की कन्या को देवी के एक-एक स्वरूप का प्रतीक माना गया है, प्रत्येक का अपना वरदान।", "By tradition each girl aged 2 to 10 represents one form of the Devi, each carrying her own blessing.")}</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {AGE_FORMS.map((a) => (
              <div key={a.age} className="border border-line bg-parchment/60 p-4" data-testid={`kanya-age-${a.age}`}>
                <div className="flex items-baseline justify-between">
                  <p className="font-deva text-xl text-ink deva-lh">{a.deva}</p>
                  <span className="font-serif-latin text-sm text-gold">{pick("वर्ष", "Age")} {a.age}</span>
                </div>
                <p className="font-serif-latin text-sm text-ink-2">{a.en}</p>
                <p className="mt-2 text-sm leading-snug text-ink-3">{pick(a.boon_hi, a.boon_en)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Bhog */}
        <section className="mt-16 fade-up" data-testid="kanya-bhog">
          <div className="mb-6 flex items-baseline gap-3">
            <span className="h-px w-10 bg-[var(--gold)]" />
            <h2 className={`text-3xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("भोग", "The Bhog")}</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {BHOG.map((b, i) => (
              <div key={i} className="flex items-start gap-3 border border-line bg-parchment/60 p-4">
                <Utensils className="mt-0.5 h-5 w-5 shrink-0 text-saffron" />
                <p className="text-[15px] leading-snug text-ink-2">{pick(b.hi, b.en)}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        <section className="mt-16 fade-up" data-testid="kanya-steps">
          <div className="mb-6 flex items-baseline gap-3">
            <span className="h-px w-10 bg-[var(--gold)]" />
            <h2 className={`text-3xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("पूजन विधि — चरण-दर-चरण", "The ritual, step by step")}</h2>
          </div>
          <ol className="space-y-4">
            {STEPS.map((s, i) => (
              <li key={i} className="flex gap-4 border-b border-line pb-4" data-testid={`kanya-step-${i}`}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--gold)]/50 font-serif-latin text-sm text-gold">{i + 1}</span>
                <p className="text-[17px] leading-relaxed text-ink-2">{pick(s.hi, s.en)}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Note + CTA */}
        <section className="mt-14 border-l-2 border-[var(--gold)]/60 bg-parchment/60 px-6 py-5">
          <p className="flex items-start gap-2 text-[15px] italic text-ink-2">
            <Heart className="mt-0.5 h-4 w-4 shrink-0 text-saffron" />
            {pick("भाव ही प्रधान है — कन्याओं के प्रति आदर, प्रेम एवं देवी-दृष्टि से किया गया सरल पूजन भी पूर्ण फलदायी है।", "The feeling (bhava) matters most — even a simple honouring, done with respect, love and the vision of the Devi in every child, is fully complete.")}
          </p>
        </section>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/rituals/navratri-durga-puja-vidhi" data-testid="kanya-pujavidhi" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]"><Flame className="h-4 w-4" /> {pick("पूर्ण दुर्गा पूजा विधि", "Full Durga Pooja Vidhi")}</Link>
          <Link to="/navratri" data-testid="kanya-navratri" className="inline-flex items-center gap-2 border border-line px-6 py-3 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">{pick("नवरात्रि पर लौटें", "Back to Navratri")} <ArrowRight className="h-4 w-4" /></Link>
        </div>

        <Divider className="mx-auto my-16 max-w-xs" />
      </div>
    </div>
  );
}
